import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import InternalActivityTable from "./internal_activities/InternalActivityTable";
import { EMPTY_FILTERS } from "./internal_activities/InternalActivitiesTableFilters";

const TRACKABLE_PATH = {
  Candidate:       (id) => `/internal/candidates/${id}`,
  Recruiter:       (id) => `/internal/recruiters/${id}`,
  Company:         (id) => `/internal/companies/${id}`,
  ProfilePipeline: (id) => `/internal/pipelines/${id}`,
  BlogPost:        (id) => `/internal/blog_posts/${id}`,
  JobApplication:  (id) => `/internal/job_applications/${id}`,
  Credit:          ()   => "/internal/credits",
};

const InternalActivitiesIndexPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleActivityClick = useCallback((activity) => {
    const { type, id } = activity?.trackable || {};
    const pathFn = TRACKABLE_PATH[type];
    if (pathFn) navigate(pathFn(id));
  }, [navigate]);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    sh.get("/internal/search/internal_users", { params: { all: true } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.internal_users || [];
        setUsers(data);
      })
      .catch(() => {});
  }, []);

  const fetchActivities = useCallback(() => {
    setLoading(true);
    const params = { page, limit: perPage };
    if (searchQuery) params.query = searchQuery;
    if (filters.action?.length) params.activity_action = filters.action;
    if (filters.user?.length) params.user_id = filters.user;
    if (filters.trackableType?.length) params.trackable_type = filters.trackableType;

    sh.get("/internal/search/internal_activities", { params })
      .then((res) => {
        setActivities(res.data.data || []);
        setTotalCount(res.data.pagy?.count || 0);
      })
      .catch(() => {
        enqueueSnackbar("Failed to fetch activities", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [page, perPage, searchQuery, filters, enqueueSnackbar]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handlePageChange = useCallback((newPage) => setPage(newPage), []);
  const handleRowsPerPageChange = useCallback((val) => { setPerPage(val); setPage(1); }, []);
  const handleFiltersChange = useCallback((next) => { setFilters(next); setPage(1); }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <InternalActivityTable
        activities={activities}
        onQueryChange={(q) => { setSearchQuery(q); setPage(1); }}
        onActivityClick={handleActivityClick}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        users={users}
        page={page}
        totalCount={totalCount}
        rowsPerPage={perPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />
    </Box>
  );
};

export default InternalActivitiesIndexPage;